import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { X, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Floating "🟢 Someone in Bangalore just got a quote 2 min ago" toast that
 * pops bottom-left every 35–60s. Pulls from /api/quote/recent-pulses.
 * Falls back to silent (no render) if no real submissions exist.
 *
 * Designed to be mounted ONCE on the public quote-services page. Auto-paces
 * itself, auto-rotates through up to 20 recent items, and offers a session-
 * scoped "hide" link so it doesn't annoy repeat visitors.
 */
export default function QuotePulse() {
  const [items, setItems] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showing, setShowing] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("shanso_pulse_hidden") === "1");
  const timerRef = useRef(null);

  // Fetch once on mount + refresh every 5 min so new submissions appear
  useEffect(() => {
    let mounted = true;
    function load() {
      axios.get(`${API}/quote/recent-pulses`)
        .then((r) => { if (mounted) setItems(r.data?.items || []); })
        .catch(() => {});
    }
    load();
    const refreshInt = setInterval(load, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(refreshInt); };
  }, []);

  // Drive show/hide rotation
  useEffect(() => {
    if (hidden || items.length === 0) return;
    let cancelled = false;

    function scheduleNext(initial = false) {
      if (cancelled) return;
      const delay = initial ? 6_000 : 30_000 + Math.random() * 30_000;  // first one at 6s, then 30–60s
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        setShowing(true);
        // Auto-hide after 6 seconds
        setTimeout(() => {
          if (cancelled) return;
          setShowing(false);
          setCurrentIdx((i) => (i + 1) % items.length);
          scheduleNext(false);
        }, 6_000);
      }, delay);
    }
    scheduleNext(true);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [items, hidden]);

  function hideForSession() {
    sessionStorage.setItem("shanso_pulse_hidden", "1");
    setHidden(true);
    setShowing(false);
  }

  if (hidden || items.length === 0 || !showing) return null;
  const item = items[currentIdx];
  if (!item) return null;

  return (
    <div
      data-testid="quote-pulse-toast"
      role="status"
      aria-live="polite"
      className="fixed bottom-5 left-5 z-40 max-w-[300px] sm:max-w-[340px] rounded-xl border border-cyan-400/30 backdrop-blur-md shadow-2xl"
      style={{
        background: "linear-gradient(135deg, rgba(34,211,238,0.10), rgba(24,24,27,0.95))",
        animation: "pulseSlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="relative shrink-0 mt-0.5">
          <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 animate-ping"></span>
          <span className="relative block w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-zinc-100 leading-snug">
            <span className="text-cyan-200 font-semibold">Someone in {item.location}</span>{" "}
            just got a quote for {item.project_label}
          </p>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            <CheckCircle2 size={10} className="inline mr-1 text-emerald-400 -mt-0.5"/>
            {formatAge(item.age_seconds)} ago
          </p>
        </div>
        <button onClick={hideForSession} aria-label="Hide" data-testid="hide-pulse-btn"
          className="text-zinc-600 hover:text-zinc-300 shrink-0">
          <X size={14}/>
        </button>
      </div>
      <style>{`
        @keyframes pulseSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function formatAge(sec) {
  if (sec < 60) return "Just now";
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  if (sec < 86400) return `${Math.round(sec / 3600)} hr`;
  const days = Math.round(sec / 86400);
  return `${days} day${days === 1 ? "" : "s"}`;
}
